import { useEffect, useRef, useState } from 'react';
import { sseReconnectDelay } from '../../../../utils/sseReconnect';

export type WorkflowRunStepSnapshot = {
  step_id: string;
  status?: string;
  attempts?: number;
  updated_at?: string;
  last_error?: string;
  output?: any;
  job_id?: string;
  job_type?: string;
  /** Optional live progress payload (from jobs-progress stream), best-effort. */
  progress?: any;
};

export type WorkflowRunSnapshot = {
  seq: number;
  run_id: string;
  status?: string;
  percent?: number;
  polled_at?: string;
  unchanged?: boolean;
  counts?: {
    total: number;
    succeeded: number;
    failed: number;
    running: number;
    ready: number;
    pending: number;
  };
  steps?: WorkflowRunStepSnapshot[];
  run?: any;
  summary?: any;
};

export interface UseWorkflowRunStreamResult {
  snapshot: WorkflowRunSnapshot | null;
  loading: boolean;
  error: any;
  usingSSE: boolean;
  reset(): void;
}

export function useWorkflowRunStream(runId: string | null | undefined): UseWorkflowRunStreamResult {
  const [snapshot, setSnapshot] = useState<WorkflowRunSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [usingSSE, setUsingSSE] = useState(false);

  const esRef = useRef<EventSource | null>(null);
  const sseAttemptRef = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reset = () => {
    setSnapshot(null);
    setError(null);
    setLoading(false);
    setUsingSSE(false);
    if (esRef.current) {
      try { esRef.current.close(); } catch {}
      esRef.current = null;
    }
  };

  useEffect(() => {
    reset();
  }, [runId]);

  useEffect(() => {
    if (!runId) return;
    if (typeof EventSource === 'undefined') {
      setError(new Error('EventSource is not available in this environment'));
      return;
    }

    setLoading(true);
    setUsingSSE(true);
    setError(null);

    const es = new EventSource(`/bff/orchestrator/runs/${encodeURIComponent(runId)}/stream`);
    esRef.current = es;

    // Fired when the connection is established (including after automatic reconnects).
    es.onopen = () => {
      setError(null);
    };

    es.addEventListener('hello', (_ev: MessageEvent) => {
      setLoading(false);
      setError(null);
    });

    es.addEventListener('snapshot', (ev: MessageEvent) => {
      try {
        const data = JSON.parse(ev.data || '{}');
        setSnapshot(data);
        setLoading(false);
        setError(null);
      } catch (e) {
        // ignore parse errors
      }
    });

    // Server-side failures (upstream timeout, orchestrator unavailable, etc.) are emitted
    // as SSE events; without this handler the UI can look "stuck" forever.
    const onServerError = (ev: MessageEvent) => {
      try {
        const data = JSON.parse(ev.data || '{}');
        const msg = data?.error || data?.message || ev.data || 'Workflow backend error';
        setError(new Error(String(msg)));
        setLoading(false);
      } catch {
        setError(new Error(String((ev as any)?.data || 'Workflow backend error')));
        setLoading(false);
      }
    };
    // Prefer non-reserved name, but keep legacy compatibility.
    es.addEventListener('server_error', onServerError as any);
    es.addEventListener('error', onServerError as any);

    es.addEventListener('done', (ev: MessageEvent) => {
      try {
        const data = JSON.parse(ev.data || '{}');
        setSnapshot((prev) => ({ ...(prev || ({} as any)), ...data }));
      } catch {}
      try { es.close(); } catch {}
    });

    // H3: Close and manually reconnect with jitter to prevent thundering-herd.
    // Browser's default ~3s deterministic retry causes all tabs to hit the
    // server simultaneously after a disconnect.
    es.onerror = () => {
      setLoading(false);
      setError(new Error('Workflow stream connection interrupted (will retry)'));
      try { es.close(); } catch {}
      esRef.current = null;
      const delay = sseReconnectDelay(sseAttemptRef.current);
      sseAttemptRef.current++;
      reconnectTimer.current = setTimeout(() => {
        reconnectTimer.current = null;
        // Trigger effect re-run by resetting refs — React effect deps on runId
        // will re-create the EventSource on next render if runId is stable.
        // Force reconnect by clearing error so the effect re-runs.
        setError(null);
        setLoading(true);
        const es2 = new EventSource(`/bff/orchestrator/runs/${encodeURIComponent(runId!)}/stream`);
        esRef.current = es2;
        es2.onopen = () => { sseAttemptRef.current = 0; setError(null); };
        es2.addEventListener('hello', () => { setLoading(false); setError(null); });
        es2.addEventListener('snapshot', (ev: MessageEvent) => {
          try { setSnapshot(JSON.parse(ev.data || '{}')); setLoading(false); setError(null); } catch {}
        });
        es2.addEventListener('done', (ev: MessageEvent) => {
          try { setSnapshot(p => ({ ...(p || {} as any), ...JSON.parse(ev.data || '{}') })); } catch {}
          try { es2.close(); } catch {}
        });
        es2.onerror = es.onerror; // reuse the same jittered handler
      }, delay);
    };

    return () => {
      try { es.close(); } catch {}
      if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null; }
    };
  }, [runId]);

  return { snapshot, loading, error, usingSSE, reset };
}
